import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, X, Send, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { logActivity } from '@/lib/activityLog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to convert relative avatar URLs to absolute
const getAvatarUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${window.location.origin}${url}`;
};

const MESSAGE_CATEGORIES = [
  { value: 'Meal plan / recipes', label: 'Meal plan / recipes' },
  { value: 'Logging or tracking', label: 'Logging or tracking' },
  { value: 'Eating out / free meals', label: 'Eating out / free meals' },
  { value: 'Weight changes', label: 'Weight changes' },
  { value: 'Billing / account', label: 'Billing / account' },
  { value: 'Something else', label: 'Something else' }
];

/**
 * Floating chat bubble + popup chat window.
 * - Fixed bottom-right, visible on all client pages (hidden on /app/messages).
 * - Unread badge auto-updates and disappears after opening (backend marks read on fetch).
 * - Click bubble -> popup with conversation + compose.
 */
export default function FloatingChatBubble() {
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [coach, setCoach] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ category: '', body: '' });
  const threadRef = useRef(null);

  const fetchUnread = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setUnread(res.data?.unread_messages || 0);
    } catch (_) { /* silent */ }
  };

  const fetchConversation = async () => {
    setLoading(true);
    try {
      const [msgsRes, meRes] = await Promise.all([
        axios.get(`${API}/messages`),
        axios.get(`${API}/auth/me`)
      ]);
      setMessages(msgsRes.data || []);
      setMe(meRes.data);
      // Marking happens server-side on GET /messages, so refresh the unread count
      setUnread(0);
      if (meRes.data?.coach_id) {
        try {
          const coachRes = await axios.get(`${API}/users/${meRes.data.coach_id}`);
          setCoach(coachRes.data);
        } catch (_) { /* silent */ }
      }
    } catch (err) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    const onFocus = () => fetchUnread();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [location.pathname]);

  // When the popup opens, load conversation
  useEffect(() => {
    if (open) fetchConversation();
  }, [open]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Lock body scroll on mobile when popup is open
  useEffect(() => {
    if (open && window.innerWidth < 640) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.body.trim()) { toast.error('Please type a message'); return; }
    setSending(true);
    try {
      const params = { to_user_id: coach?.id || '', body: form.body };
      if (form.category) params.category = form.category;
      await axios.post(`${API}/messages`, null, { params });
      toast.success('Message sent');
      setForm({ category: '', body: '' });
      fetchConversation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Hide entirely on the dedicated messages page (redundant there)
  if (location.pathname.startsWith('/app/messages')) return null;

  const sortedMessages = [...messages].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  return (
    <>
      {/* The bubble */}
      {!open && (
        <button
          data-testid="floating-chat-bubble"
          onClick={() => { setOpen(true); logActivity('messages_viewed'); }}
          aria-label={unread > 0 ? `${unread} unread messages` : 'Open chat'}
          className="fixed bottom-6 right-6 z-50 group"
          style={{ animation: unread > 0 ? 'chatPulse 2s ease-in-out infinite' : 'none' }}
        >
          <span className="hidden md:inline-block absolute right-[70px] top-1/2 -translate-y-1/2 bg-white text-gray-900 text-sm font-medium px-4 py-2 rounded-full shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-gray-200">
            {unread > 0 ? `You have ${unread} new message${unread === 1 ? '' : 's'}` : 'Chat with your coach'}
          </span>
          <div className="relative w-14 h-14 rounded-full bg-[#1a61e8] hover:bg-[#1553c9] text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95">
            <MessageSquare className="w-6 h-6" fill="currentColor" strokeWidth={0} />
            {unread > 0 && (
              <span
                data-testid="floating-chat-bubble-badge"
                className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center ring-2 ring-white"
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </div>
        </button>
      )}

      {/* The popup chat window */}
      {open && (
        <div
          data-testid="floating-chat-popup"
          className="fixed z-[110] bg-white shadow-2xl border border-gray-200 flex flex-col
                     inset-x-0 top-0 h-[100dvh] sm:inset-auto sm:top-auto sm:bottom-6 sm:right-6 sm:w-[380px] sm:h-[560px]
                     sm:rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#1a61e8] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full overflow-hidden bg-white/20 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0 ring-2 ring-white/40">
                {coach?.avatar_url ? (
                  <img src={getAvatarUrl(coach.avatar_url)} alt="Coach" className="w-full h-full object-cover" />
                ) : coach?.first_name ? (
                  <span>{coach.first_name[0]?.toUpperCase()}</span>
                ) : (
                  <MessageSquare className="w-4 h-4" fill="currentColor" strokeWidth={0} />
                )}
              </div>
              <div>
                <div className="font-semibold text-sm">
                  {coach ? `Coach ${coach.first_name}` : 'Your Coach'}
                </div>
                <div className="text-xs text-blue-100">I&apos;ll reply within 24 hours</div>
              </div>
            </div>
            <button
              data-testid="floating-chat-close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              className="p-1.5 rounded-full hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages thread */}
          <div ref={threadRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {loading ? (
              <div className="text-center py-8 text-gray-400 text-sm">Loading...</div>
            ) : sortedMessages.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <MessageSquare className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">No messages yet. Say hi!</p>
              </div>
            ) : (
              sortedMessages.map((m) => {
                const isFromMe = m.from_user_id === me?.id;
                return (
                  <div key={m.id} className={`flex items-end gap-2 ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                    {!isFromMe && (
                      <div className="w-7 h-7 rounded-full overflow-hidden bg-[#1a61e8] text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0 self-end mb-5">
                        {coach?.avatar_url ? (
                          <img src={getAvatarUrl(coach.avatar_url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span>{coach?.first_name?.[0]?.toUpperCase() || 'C'}</span>
                        )}
                      </div>
                    )}
                    <div className="max-w-[80%]">
                      {isFromMe && m.category && (
                        <div className="flex justify-end mb-1">
                          <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                            {m.category}
                          </Badge>
                        </div>
                      )}
                      <div
                        className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                          isFromMe
                            ? 'bg-[#1a61e8] text-white rounded-br-md'
                            : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                        }`}
                      >
                        {m.body}
                      </div>
                      <div className={`flex items-center gap-1 mt-1 text-[10px] text-gray-400 ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                        <Clock className="h-3 w-3" />
                        {new Date(m.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Compose */}
          <form onSubmit={handleSend} className="border-t bg-white p-3 space-y-2 flex-shrink-0">
            <Select
              value={form.category}
              onValueChange={(value) => setForm({ ...form, category: value })}
            >
              <SelectTrigger data-testid="floating-chat-category" className="h-9 text-sm">
                <SelectValue placeholder="Category (optional)..." />
              </SelectTrigger>
              <SelectContent>
                {MESSAGE_CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2 items-end">
              <Textarea
                data-testid="floating-chat-textarea"
                placeholder="Type a message..."
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                onFocus={() => {
                  // Delay so the iOS keyboard animation settles, then snap the thread to bottom
                  setTimeout(() => {
                    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
                  }, 350);
                }}
                rows={2}
                style={{ fontSize: '16px' }}
                className="flex-1 resize-none min-h-[44px]"
              />
              <Button
                data-testid="floating-chat-send"
                type="submit"
                disabled={sending || !form.body.trim()}
                className="bg-[#1a61e8] hover:bg-[#1553c9] h-11 w-11 p-0 flex-shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        @keyframes chatPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
      `}</style>
    </>
  );
}
